import React from 'react';
import { motion } from 'framer-motion';
import { FlaskConicalIcon, MegaphoneIcon, MonitorIcon, UserRoundIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const CANDIDATES = [{
  label: 'Webinar',
  detail: 'Turn one event into short evergreen chapters.',
  icon: VideoIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/c5JnfxCUQWGVrx13N99WPc/Screenshot_2026-09-21_at_12.00.33.png',
    alt: 'Treatment-algorithm slide from a recorded webinar session'
  }]
}, {
  label: 'Website',
  detail: 'Add interaction to content already attracting visitors.',
  icon: MonitorIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/2yu7P6mkEn7MHj3uk2nVvt/image.png',
    alt: 'Website module asking “When is it chronic?”'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/roFnSkUQaYKQZBZAudpijc/Screenshot_2026-09-21_at_11.52.50.png',
    alt: 'Anzupgo product page with interactive hotspots'
  }]
}, {
  label: 'Campaign content',
  detail: 'Turn approved material into a short interactive campaign.',
  icon: MegaphoneIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/dmaZ1QSTb4ao1gmgvb3v2T/image.png',
    alt: 'Campaign screen inviting HCPs to choose what they would do with extra time'
  }]
}, {
  label: 'Congress',
  detail: 'Create pre-, during- and post-event engagement.',
  icon: UsersIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/gJSVHdrbcH9vuLtjvPsesJ/Screenshot_2026-09-21_at_12.03.24.png',
    alt: 'Congress reflection Snak opening screen for ERA 2026'
  }]
}, {
  label: 'Clinical / dosing content',
  detail: 'Make complex information easier to explore.',
  icon: FlaskConicalIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/fHLCWN7UaejSUv4dnPHFZ5/Screenshot_2026-09-21_at_11.57.57.png',
    alt: 'Dosing screen explaining dose by renal function'
  }]
}, {
  label: 'Patient content',
  detail: 'Adapt approved HCP material into guided patient experiences.',
  icon: UserRoundIcon,
  media: [{
    src: 'https://cdn.magicpatterns.com/uploads/63Tk5nbzZ7DYCCHyPLGCzo/Screenshot_2026-09-21_at_12.09.33.png',
    alt: 'Spanish-language patient guide showing step one of applying Anzupgo'
  }]
}] as const;
export function SlideWhatToSnakify({}: SlideProps) {
  return <InteriorSlide banner={<SlideBanner data-id="element-195" data-mp="kfkaxmvmzbtj">
          Snakification is not about the asset type. It is about making
          important information easier to notice, understand and act on.
        </SlideBanner>} data-id="element-194" data-mp="kcg1a9zejnvh">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 flex-col gap-[clamp(0.5rem,1vw,0.9rem)] lg:flex-row lg:items-end lg:justify-between lg:gap-10" data-id="element-196" data-mp="351lvuzmzg96z">
        <div className="min-w-0" data-id="element-197" data-mp="1kjusoi7kuxgo">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-198" data-mp="1pladqa9r93z2">
            The principle
          </p>
          <h2 className="max-w-3xl text-[clamp(1.7rem,3.2vw,2.9rem)] font-extrabold uppercase leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-199" data-mp="1ud1yg468cfha">
            If it matters,{' '}
            <span className="text-[var(--brand)]" data-id="element-200" data-mp="23b46v4samael">
              make it easier to engage with.
            </span>
          </h2>
        </div>
        <p className="max-w-sm flex-shrink-0 text-[clamp(0.82rem,1.1vw,1rem)] font-semibold leading-snug text-[var(--muted)] lg:text-right" data-id="element-201" data-mp="2dal6p5kv5u2n">
          Anything important enough to earn HCP attention and understanding can
          be Snakified.
        </p>
      </motion.header>

      <p className="mt-[clamp(0.7rem,1.4vw,1.2rem)] flex-shrink-0 text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-202" data-mp="iyoc53r4k83d">
        For example
      </p>

      <div className="mt-[clamp(0.5rem,0.9vw,0.8rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,0.95rem)] sm:grid-cols-2 lg:grid-cols-3" data-id="element-203" data-mp="24eco472mr8hg">
        {CANDIDATES.map((candidate, index) => <motion.article key={candidate.label} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16 + index * 0.07,
        duration: 0.45
      }} className="flex min-h-0 flex-col rounded-2xl border border-[var(--fg)]/10 bg-[var(--surface)] p-[clamp(0.75rem,1.3vw,1.25rem)]" data-id="element-204" data-mp="2edjwsgyf6ey8" data-mpi={index}>
            <div className="flex min-h-0 flex-1 items-center gap-[clamp(0.5rem,1vw,0.9rem)]" data-id="element-205" data-mp="1unzsnjta1uwj">
              <span className="flex h-[clamp(2rem,2.6vw,2.6rem)] w-[clamp(2rem,2.6vw,2.6rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-206" data-mp="16y5jbc862oh1">
                <candidate.icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] text-white" aria-hidden="true" data-id="element-207" data-mp="styemkoq11bc" />
              </span>
              <div className="flex min-h-0 flex-1 items-center justify-end gap-[clamp(0.3rem,0.6vw,0.6rem)] self-stretch py-1" data-id="element-208" data-mp="iw0ry1mifcku">
                {candidate.media.map(media => <img key={media.src} src={media.src} alt={media.alt} className={`min-h-0 w-auto rounded-md object-contain shadow-[0_6px_18px_rgba(23,32,51,0.14)] ring-1 ring-[var(--fg)]/10 ${candidate.media.length > 1 ? 'max-h-[62%] max-w-[38%]' : 'max-h-full max-w-[70%]'}`} data-id="element-209" data-mp="1bk6coodkd1ko" />)}
              </div>
            </div>

            <h3 className="mt-[clamp(0.55rem,1vw,0.9rem)] flex-shrink-0 text-[clamp(0.66rem,0.9vw,0.82rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-210" data-mp="3rbki2rrvwevo">
              {candidate.label}
            </h3>
            <p className="mt-[clamp(0.25rem,0.5vw,0.45rem)] flex-shrink-0 text-[clamp(0.85rem,1.2vw,1.15rem)] font-semibold leading-snug tracking-[-0.02em] text-[var(--fg)]" data-id="element-211" data-mp="1sutgiaz5z9w0">
              {candidate.detail}
            </p>
          </motion.article>)}
      </div>
    </InteriorSlide>;
}